import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import Header from "@/components/Header";
import YourMinute from "@/components/YourMinute";
import AdviceMeter from "@/components/AdviceMeter";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  ArrowRight, Zap, MousePointerClick, Gauge, LogOut, Target, Ban,
  ArrowUpRight, Sparkle, ShieldCheck, Clock,
} from "lucide-react";

const LOOP = ["Problem", "Clarify", "Diagnose", "Decide", "Act", "Leave", "Measure"];

function DemoSimulator() {
  const [cards, setCards] = useState([]);
  const [step, setStep] = useState("idle"); // idle|loading|clarify|result
  const [data, setData] = useState(null);
  const [activeTitle, setActiveTitle] = useState("");
  const [customText, setCustomText] = useState("");
  const panelRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => { api.get("/demo/cards").then(({ data }) => setCards(data.cards)).catch(() => {}); }, []);
  useEffect(() => { if (step !== "idle" && panelRef.current) panelRef.current.scrollIntoView({ behavior: "smooth", block: "start" }); }, [step]);

  const run = async (payload, title) => {
    setActiveTitle(title); setStep("loading");
    try {
      const { data: res } = await api.post("/demo/analyze", payload);
      setData(res);
      setStep(res.needs_clarification ? "clarify" : "result");
    } catch { setStep("idle"); }
  };

  const runCustom = () => {
    if (customText.trim().length < 3) return;
    run({ text: customText }, customText);
  };

  return (
    <section id="demo" className="relative border-t border-white/8 bg-[#0B0C0E] py-20 sm:py-28">
      <div className="mx-auto max-w-6xl px-5 sm:px-8">
        <div className="max-w-2xl">
          <span className="eyebrow flex items-center gap-2"><MousePointerClick className="h-3.5 w-3.5" /> 20-second demo · no account needed</span>
          <h2 className="font-head mt-3 text-3xl font-black tracking-tight text-white sm:text-4xl">See a real decision get closed.</h2>
          <p className="mt-3 text-lg text-neutral-400">Click any real founder blocker. Watch MinuteSwap diagnose it and hand back one decisive move — in seconds.</p>
        </div>

        <div className="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {cards.map((c, i) => (
            <motion.button
              key={c.id}
              data-testid={`demo-card-${c.id}`}
              initial={{ opacity: 0, y: 14 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
              transition={{ delay: 0.05 * i }}
              onClick={() => run({ card_id: c.id }, c.title)}
              className="group flex flex-col rounded-2xl border border-white/10 bg-[var(--surface)] p-5 text-left transition-all hover:border-[var(--amber)]/40 hover:bg-[var(--surface-raised)]"
            >
              <span className="font-mono2 text-[10px] font-semibold uppercase tracking-[0.18em] text-[var(--amber)]">{c.tag}</span>
              <p className="mt-2.5 text-[15px] font-semibold leading-snug text-white">{c.title}</p>
              <p className="mt-2 text-sm leading-relaxed text-neutral-500">{c.context}</p>
              <span className="mt-4 flex items-center gap-1.5 text-xs font-medium text-neutral-400 transition-colors group-hover:text-[var(--amber)]">
                Get the move <ArrowRight className="h-3.5 w-3.5" />
              </span>
            </motion.button>
          ))}
        </div>

        <div className="mt-6 flex flex-col gap-3 rounded-2xl border border-white/10 bg-[var(--surface)] p-3 sm:flex-row">
          <Input data-testid="demo-custom-input" value={customText} onChange={(e) => setCustomText(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && runCustom()}
            placeholder="…or type your own blocker to try it live" className="border-0 bg-transparent text-white placeholder:text-neutral-600 focus-visible:ring-0" />
          <Button data-testid="demo-custom-btn" onClick={runCustom} className="btn-amber shrink-0 rounded-xl px-6">Try it</Button>
        </div>

        <div ref={panelRef}>
          <AnimatePresence mode="wait">
            {step === "loading" && (
              <motion.div key="l" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="mt-8 flex flex-col items-center py-16">
                <div className="relative h-14 w-14"><div className="absolute inset-0 animate-spin rounded-full border-2 border-white/10 border-t-[var(--amber)]" /><Sparkle className="absolute inset-0 m-auto h-5 w-5 text-[var(--amber)]" /></div>
                <p className="font-mono2 mt-5 text-xs uppercase tracking-[0.25em] text-neutral-400">Compiling decision</p>
              </motion.div>
            )}

            {step === "clarify" && data && (
              <motion.div key="c" initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
                className="mt-8 rounded-2xl border border-white/10 bg-[var(--surface)] p-7">
                <span className="eyebrow">Ask Sharpener · one question only</span>
                <h3 className="font-head mt-2 text-2xl font-extrabold text-white">{data.clarification_question}</h3>
                <div className="mt-5 space-y-2.5">
                  {data.clarification_options?.map((o, i) => (
                    <button key={i} data-testid={`demo-sharpener-${i}`} onClick={() => run({ text: activeTitle || customText, clarification_answer: o }, activeTitle)}
                      className="group flex w-full items-center justify-between rounded-xl border border-white/10 bg-[var(--surface-raised)] px-5 py-3.5 text-left hover:border-[var(--amber)]/50">
                      <span className="text-neutral-200 group-hover:text-white">{o}</span>
                      <ArrowRight className="h-4 w-4 text-neutral-600 group-hover:text-[var(--amber)]" />
                    </button>
                  ))}
                </div>
              </motion.div>
            )}

            {step === "result" && data && (
              <motion.div key="r" initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} className="mt-8">
                {data.blocker_text && (
                  <div className="mb-4 rounded-xl border border-white/10 bg-[var(--surface-raised)] px-5 py-3">
                    <span className="eyebrow">The blocker</span>
                    <p className="mt-1 text-sm text-neutral-300">{data.blocker_text}</p>
                  </div>
                )}
                <div className="mb-5 rounded-xl border border-white/10 bg-[var(--surface-raised)] px-5 py-4"><AdviceMeter state={data.advice_meter} /></div>
                <YourMinute category={data.category} recommendation={data.recommendation} />
                <div className="mt-6 flex flex-col items-center gap-4 rounded-2xl border border-[var(--amber)]/25 bg-[var(--amber)]/[0.05] p-7 text-center">
                  <p className="font-head text-xl font-bold text-white">This is what one minute looks like.</p>
                  <p className="text-sm text-neutral-400">Create an account to commit to moves, track outcomes and build your MinuteScore.</p>
                  <Button data-testid="demo-signup-btn" onClick={() => navigate("/register")} className="btn-amber rounded-full px-7 py-6 text-base">
                    Get my next move <ArrowRight className="ml-1 h-4 w-4" />
                  </Button>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </section>
  );
}

export default function Landing() {
  const navigate = useNavigate();
  return (
    <div className="relative min-h-screen">
      <Header />

      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="pointer-events-none absolute inset-0" style={{ background: "radial-gradient(720px circle at 15% -5%, rgba(255,107,53,0.12), transparent 55%)" }} />
        <div className="relative mx-auto grid max-w-7xl items-center gap-12 px-5 py-16 sm:px-8 sm:py-24 lg:grid-cols-12">
          <div className="lg:col-span-7">
            <motion.span initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="inline-flex items-center gap-2 rounded-full border border-white/12 bg-[var(--surface-raised)] px-3.5 py-1.5">
              <span className="h-1.5 w-1.5 rounded-full bg-[var(--amber)]" />
              <span className="font-mono2 text-[11px] uppercase tracking-[0.18em] text-neutral-300">The AI that wants you to close it</span>
            </motion.span>

            <motion.h1 initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 }}
              className="font-head mt-6 text-4xl font-black leading-[1.03] tracking-tight text-white sm:text-5xl lg:text-6xl">
              What is blocking your business right now?
            </motion.h1>

            <motion.p initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.12 }}
              className="mt-5 max-w-xl text-lg text-neutral-300 sm:text-xl">
              Bring one business blocker. Get one decisive next move. Take action. Leave.
            </motion.p>

            <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.2 }}
              className="font-mono2 mt-4 text-sm text-neutral-500">
              No endless chats. No infinite feed. No 47-step strategy. <span className="text-neutral-300">One problem. One move.</span>
            </motion.p>

            <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.28 }}
              className="mt-9 flex flex-col gap-3 sm:flex-row">
              <Button data-testid="hero-primary-cta-button" onClick={() => navigate("/register")} className="btn-amber rounded-full px-7 py-6 text-base font-bold">
                GET MY NEXT MOVE <ArrowRight className="ml-1 h-5 w-5" />
              </Button>
              <Button data-testid="hero-demo-cta-button" variant="outline" onClick={() => document.getElementById("demo")?.scrollIntoView({ behavior: "smooth" })}
                className="rounded-full border-white/15 px-7 py-6 text-base text-white hover:bg-white/5">
                TRY A 20-SECOND DEMO
              </Button>
            </motion.div>

            <p className="mt-6 max-w-lg text-sm leading-relaxed text-neutral-500">
              MinuteSwap analyses one business decision at a time and gives you the next action worth taking — then tells you to go do it.
            </p>
          </div>

          <motion.div initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: 0.2, duration: 0.7 }}
            className="lg:col-span-5">
            <div className="rounded-2xl border border-white/10 bg-[var(--surface)] p-6 amber-glow">
              <div className="flex items-center justify-between">
                <span className="eyebrow">Your Minute · preview</span>
                <span className="rounded-full bg-[var(--emerald)]/12 px-2.5 py-1 font-mono2 text-[10px] font-semibold uppercase text-[var(--emerald)]">Confidence: High</span>
              </div>
              <div className="mt-5 space-y-4">
                <div className="flex items-start gap-2.5">
                  <span className="mt-0.5 flex h-6 w-6 items-center justify-center rounded bg-[var(--amber)]/15"><Target className="h-3.5 w-3.5 text-[var(--amber)]" /></span>
                  <div><span className="font-mono2 text-[10px] uppercase tracking-wider text-[var(--amber)]">Do this</span><p className="text-sm text-neutral-200">Raise new-customer pricing to $199 today. Grandfather existing users for 90 days.</p></div>
                </div>
                <div className="flex items-start gap-2.5">
                  <span className="mt-0.5 flex h-6 w-6 items-center justify-center rounded bg-rose-500/15"><Ban className="h-3.5 w-3.5 text-[var(--crimson)]" /></span>
                  <div><span className="font-mono2 text-[10px] uppercase tracking-wider text-[var(--crimson)]">Avoid this</span><p className="text-sm text-neutral-400">A timid $10 bump that leaves obvious value on the table.</p></div>
                </div>
                <div className="flex items-start gap-2.5">
                  <span className="mt-0.5 flex h-6 w-6 items-center justify-center rounded bg-[var(--emerald)]/15"><Clock className="h-3.5 w-3.5 text-[var(--emerald)]" /></span>
                  <div><span className="font-mono2 text-[10px] uppercase tracking-wider text-[var(--emerald)]">Next 15 minutes</span><p className="text-sm text-neutral-400">Update the pricing page and draft the grandfather email.</p></div>
                </div>
              </div>
              <div className="mt-5 border-t border-white/8 pt-4"><AdviceMeter state="enough_information" compact /></div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* LOOP STRIP */}
      <section className="border-y border-white/8 bg-[var(--surface)]/40">
        <div className="mx-auto flex max-w-7xl flex-wrap items-center justify-center gap-x-3 gap-y-2 px-5 py-5 sm:px-8">
          {LOOP.map((s, i) => (
            <React.Fragment key={s}>
              <span className="font-mono2 text-xs uppercase tracking-wider text-neutral-400">{s}</span>
              {i < LOOP.length - 1 && <ArrowRight className="h-3 w-3 text-neutral-700" />}
            </React.Fragment>
          ))}
        </div>
      </section>

      <DemoSimulator />

      {/* PHILOSOPHY */}
      <section className="border-t border-white/8 py-20 sm:py-24">
        <div className="mx-auto max-w-6xl px-5 sm:px-8">
          <div className="grid gap-5 sm:grid-cols-3">
            {[
              { icon: Zap, t: "One problem. One move.", d: "No 15-item lists. No endless threads. MinuteSwap hands you the single next action worth taking." },
              { icon: Gauge, t: "The Advice Meter", d: "Ask the same thing three ways and it calls it: you're not gathering information, you're avoiding a decision." },
              { icon: LogOut, t: "It tells you to leave", d: "The whole product is built to get you back to work fast. We win when you spend less time here." },
            ].map((c, i) => (
              <motion.div key={i} initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: 0.08 * i }}
                className="rounded-2xl border border-white/10 bg-[var(--surface)] p-7">
                <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-[var(--amber)]/12"><c.icon className="h-5 w-5 text-[var(--amber)]" /></span>
                <h3 className="font-head mt-5 text-xl font-bold text-white">{c.t}</h3>
                <p className="mt-2 text-sm leading-relaxed text-neutral-400">{c.d}</p>
              </motion.div>
            ))}
          </div>

          <div className="mt-14 flex flex-col items-center rounded-3xl border border-white/10 bg-[var(--surface)] p-10 text-center sm:p-14">
            <ShieldCheck className="h-6 w-6 text-[var(--amber)]" />
            <h3 className="font-head mt-5 text-3xl font-black tracking-tight text-white sm:text-4xl">Stop collecting answers. Start testing decisions.</h3>
            <p className="mt-4 max-w-lg text-neutral-400">Advice without action is another form of procrastination. Bring your blocker and get moving.</p>
            <Button data-testid="footer-cta-btn" onClick={() => navigate("/register")} className="btn-amber mt-7 rounded-full px-8 py-6 text-base">
              What's blocking you? <ArrowUpRight className="ml-1 h-5 w-5" />
            </Button>
          </div>
        </div>
      </section>

      <footer className="border-t border-white/8 py-10">
        <div className="mx-auto flex max-w-7xl flex-col items-center justify-between gap-4 px-5 sm:flex-row sm:px-8">
          <span className="font-head text-lg font-extrabold text-white">MinuteSwap</span>
          <span className="font-mono2 text-xs uppercase tracking-wider text-neutral-600">One problem. One move.</span>
        </div>
      </footer>
    </div>
  );
}
